import {StringValidatorInputParams} from "../../definitions/utils/validator/stringValidator/StringValidatorInputParams";
import {StringValidatorValidationType} from "../../definitions/utils/validator/stringValidator/StringValidatorValidationType";

/**
 * Contains regexes to validate string value
 * Used by validators to validate input value
 */
export class StringValidator {

    // Main function to process validation
    public static validateString(string: string, params: StringValidatorInputParams) : boolean {
        switch (params.name) {
            case StringValidatorValidationType.EMAIL: return this.validateEmail(string);
            case StringValidatorValidationType.REQUIRED: return this.validateRequired(string);
            case StringValidatorValidationType.NUMERIC: return this.validateNumeric(string);
            case StringValidatorValidationType.DOMAIN: return this.validateDomain(string);
            case StringValidatorValidationType.IPV4: return this.validateIPV4(string);
            case StringValidatorValidationType.DATE: return this.validateDate(string);
            case StringValidatorValidationType.DATE_FUTURE: return this.validateDateFuture(string);
            case StringValidatorValidationType.MAX_LENGTH: return this.validateMaxLength(string, params.meta);
            case StringValidatorValidationType.MIN_LENGTH: return this.validateMinLength(string, params.meta);
            default: throw Error('Wrong extend validation name: ' + params.name);
        }
    }

    // Check string is valid date
    private static validateDate(string: string) : boolean {

        // Check format
        const validFormat = /^(0?[1-9]|[12][0-9]|3[01])-(0?[1-9]|1[0-2])-\d\d\d\d$/.test(string);
        if (!validFormat) { return false; }

        // Check date
        const explodedDMY = string.toString().trim().split('-');
        const inputDate = Date.parse(`${explodedDMY[2]}-${explodedDMY[1]}-${explodedDMY[0]}`);
        return !Number.isNaN(inputDate);
    }

    // Check string is valid date in future
    private static validateDateFuture(string: string) : boolean {

        const valid = this.validateDate(string);
        if (!valid) { return false; }

        const explodedDMY = string.toString().trim().split('-');
        const inputDate = new Date(Date.parse(`${explodedDMY[2]}-${explodedDMY[1]}-${explodedDMY[0]}`));
        const currentDate = new Date();
        inputDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        return inputDate.getTime() >= currentDate.getTime();
    }

    // Check string is above min length
    private static validateMinLength(string: string, minLength: number) : boolean {
        const strLen = string.toString().trim().length;
        return strLen === 0 || strLen >= minLength;
    }

    // Check string is below max length
    private static validateMaxLength(string: string, maxLength: number) : boolean {
        return string.toString().trim().length <= maxLength;
    }

    // Check string contains email address format (pavelka@master.cz)
    private static validateEmail(string: string) : boolean {
        return /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)])/.test(string);
    }

    // Check if string without spaces is not empty (a)
    private static validateRequired(string: string) : boolean {
        return string.trim().length > 0;
    }

    // Check that the string contains just numbers (7878)
    private static validateNumeric(string: string) : boolean {
        if (string.trim().length === 0) { return true; }
        return /^\d+$/.test(string);
    }

    // Check is string contains a valid domain name (something.com)
    private static validateDomain(string: string) : boolean {
        return /^\b((?=[a-z0-9-]{1,63}\.)(xn--)?[a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,63}\b\.?$/.test(string);
    }

    // Check if string contains IPv4 address format (8.8.8.8)
    private static validateIPV4(string: string) : boolean {

        if (string.trim().length === 0) { return true; }
        return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(string);
    }
}


